import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionTitle, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { fmtDT } from "@/lib/format";
import { toast } from "sonner";

const RISK_CLS = {
  on_track: "bg-signal-green/15 text-signal-green",
  delayed: "bg-signal-red/15 text-signal-red",
  escalated: "bg-signal-red/25 text-signal-red font-bold",
};
const fmtStatus = (s) => (s || "").replace(/_/g, " ").toUpperCase();

export default function SalesTower() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [nurture, setNurture] = useState([]);

  const load = () => {
    api.get("/sales-tower").then((r) => setData(r.data));
    api.get("/repeat-nurture").then((r) => setNurture(r.data.filter((n) => n.status !== "done"))).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const completeNurture = async (id, outcome) => {
    try {
      await api.post(`/repeat-nurture/${id}/complete`, { outcome });
      toast.success(outcome === "postponed" ? "Follow-up postponed by 7 days" : "Marked done");
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  if (!data) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const buckets = Object.entries(data.buckets).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-5" data-testid="sales-tower-page">
      <SectionTitle>Sales Order Control Tower</SectionTitle>
      <p className="text-xs text-muted-foreground -mt-3">Live visibility — where is every order, who owns it, what's next.</p>

      <div className="flex flex-wrap gap-2" data-testid="tower-buckets">
        {buckets.map(([k, v]) => (
          <div key={k} className="bg-card border border-border rounded-lg px-3 py-2" data-testid={`bucket-${k}`}>
            <p className="metric text-xl font-bold">{v}</p>
            <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{fmtStatus(k)}</p>
          </div>
        ))}
        {buckets.length === 0 && <p className="text-muted-foreground text-sm">No orders yet</p>}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]" data-testid="tower-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Order", "Customer", "Stage", "Required Date", "Mode", "Owner", "Next Action", "Sales Person", "Value ₹", "Risk", "Escalations", "Last Activity"].map((h) => (
                <th key={h} className="px-3 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.bill_no} className="border-b border-border/50 hover:bg-accent/40 cursor-pointer" onClick={() => navigate(`/orders/${o.bill_no}`)} data-testid={`tower-row-${o.bill_no}`}>
                <td className="px-3 py-2.5 metric text-primary font-bold">{o.bill_no}</td>
                <td className="px-3 py-2.5">{o.customer}</td>
                <td className="px-3 py-2.5"><StatusBadge status={o.fulfilment_status} /></td>
                <td className="px-3 py-2.5 metric text-xs">{fmtDT(o.required_date) || "—"}</td>
                <td className="px-3 py-2.5 text-xs uppercase">{fmtStatus(o.delivery_mode) || "—"}</td>
                <td className="px-3 py-2.5 text-xs"><span className="font-semibold">{o.owner_team}</span>{o.owner_persons?.length > 0 && <span className="text-muted-foreground"> · {o.owner_persons.join(", ")}</span>}</td>
                <td className="px-3 py-2.5 text-xs max-w-52 truncate">{o.next_action}{o.open_task && <span className="metric text-muted-foreground"> ({o.open_task})</span>}</td>
                <td className="px-3 py-2.5 text-xs">{o.sales_person || "—"}</td>
                <td className="px-3 py-2.5 metric text-xs">₹{(o.total || 0).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${RISK_CLS[o.risk]}`} data-testid={`risk-${o.bill_no}`}>{fmtStatus(o.risk)}</span></td>
                <td className="px-3 py-2.5 metric text-xs">{o.escalations_open > 0 ? <span className="text-signal-red font-bold">{o.escalations_open}</span> : "—"}</td>
                <td className="px-3 py-2.5 metric text-xs">{fmtDT(o.last_activity)}</td>
              </tr>
            ))}
            {data.orders.length === 0 && <tr><td colSpan={12} className="px-4 py-8 text-center text-muted-foreground">No orders</td></tr>}
          </tbody>
        </table>
      </div>

      {nurture.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto" data-testid="nurture-table">
          <h3 className="text-lg font-semibold uppercase tracking-tight px-5 pt-4">Repeat-Order Follow-ups</h3>
          <table className="w-full text-sm mt-2">
            <thead><tr className="border-b border-border text-left">
              {["Customer", "Last Order", "Products", "Cycle", "Next Follow-up", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>))}
            </tr></thead>
            <tbody>
              {nurture.map((n) => {
                const due = n.next_followup <= new Date().toISOString().slice(0, 10);
                return (
                  <tr key={n.nurture_id} className="border-b border-border/50" data-testid={`nurture-${n.nurture_id}`}>
                    <td className="px-4 py-2.5 font-medium">{n.customer}</td>
                    <td className="px-4 py-2.5 metric text-xs">{n.bill_no}</td>
                    <td className="px-4 py-2.5 text-xs">{(n.products || []).join(", ")}</td>
                    <td className="px-4 py-2.5 metric text-xs">{n.reorder_cycle_days}d</td>
                    <td className={`px-4 py-2.5 metric text-xs ${due && n.status === "open" ? "text-signal-red font-bold" : ""}`}>{n.next_followup}</td>
                    <td className="px-4 py-2.5 text-xs uppercase">{n.status}</td>
                    <td className="px-4 py-2.5 space-x-1.5 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => completeNurture(n.nurture_id, "reorder_placed")} data-testid={`nurture-reorder-${n.nurture_id}`}>Reorder placed</Button>
                      <Button size="sm" variant="ghost" onClick={() => completeNurture(n.nurture_id, "postponed")} data-testid={`nurture-postpone-${n.nurture_id}`}>Postpone</Button>
                      <Button size="sm" variant="ghost" className="text-signal-red" onClick={() => completeNurture(n.nurture_id, "not_interested")} data-testid={`nurture-ni-${n.nurture_id}`}>Not interested</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
